import {Component, DestroyRef, OnDestroy} from '@angular/core';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { merge, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {Router, RouterLink} from '@angular/router';

import { AuthService } from '../../../shared/services/auth.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { AlertMessage } from '../../interfaces/alert.interface';

// Angular Material Imports
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

// Custom Components / Pipes
import { AlertsComponent } from '../../../shared/components/alerts/alerts.component';
import { CardsBasicComponent } from '../../../shared/components/cards/cards-basic/cards-basic.component';
import { LocalizeRouterPipe } from '@gilsdav/ngx-translate-router';
import {TranslateService} from "@ngx-translate/core";
import {HeaderComponent} from "../../../layouts/header/header.component";

@Component({
  selector: 'app-login-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    AlertsComponent,
    CardsBasicComponent,
    LocalizeRouterPipe,
    RouterLink,
    HeaderComponent
  ],
  templateUrl: './login-password.component.html',
  styleUrls: ['./login-password.component.css']
})
export class LoginPasswordComponent {
  isLoading: boolean = false;
  alertData: AlertMessage | null = null;

  // Reaktives Formular für E-Mail und Passwort
  loginForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required])
  });

  constructor(
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private router: Router,
    private destroyRef: DestroyRef,
    private translate: TranslateService
  ) {
    // Hier wird der DestroyRef verwendet, um automatisch zu unsubscriben
    merge(
      this.loginForm.controls['email'].valueChanges,
      this.loginForm.controls['password'].valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateErrorMessage());
  }

  login(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      const { email, password } = this.loginForm.value;
      this.authService.loginWithPassword({ email, password }).subscribe({
        next: (res: any) => {
          console.log('res', res)
          if (res.tokens) {
            this.authService.saveToken(res.tokens);
          }
          this.snackbarService.show(res.message);

          const currentLang = this.translate.currentLang || 'en';
          this.router.navigate([`/${currentLang}/user-profile`]);
        },
        error: (err: any) => {
          console.error('Login fehlgeschlagen', err);
          this.setAlertData(err.error?.error || 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.', 'error');
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    }
  }

  updateErrorMessage(): void {
    if (this.loginForm.controls['email'].hasError('required')) {
      this.setAlertData('Bitte gib Deine E-Mail-Adresse ein.', 'error');
    } else if (this.loginForm.controls['email'].hasError('email')) {
      this.setAlertData('Bitte gib eine gültige E-Mail-Adresse ein.', 'error');
    } else if (this.loginForm.controls['password'].hasError('required')) {
      this.setAlertData('Bitte gib Dein Passwort ein.', 'error');
    } else {
      this.alertData = null;
    }
  }

  private setAlertData(message: string, type: 'error' | 'success' | 'warning'): void {
    this.alertData = { message, type };
  }
}
